import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { clearAllTokens, isRefreshTokenExpired, storeTokens } from '@/lib/token-manager';
import { AuthApiService } from '@/lib/services/auth-api';

const WARN_BEFORE_MS = 5 * 60 * 1000; // warn 5 minutes before expiry
const REFRESH_EXPIRY_KEY = 'welynk_refresh_expiry';
const CHECK_INTERVAL_MS = 30 * 1000; // check every 30 seconds

function getRefreshTokenRemainingMs(): number {
  const expiry = localStorage.getItem(REFRESH_EXPIRY_KEY);
  if (!expiry) return 0;
  return Math.max(0, parseInt(expiry, 10) - Date.now());
}

export const useAutoLogout = () => {
  const navigate = useNavigate();
  const clearUser = useAuthStore((state) => state.clearUser);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showingExpiryWarning, setShowingExpiryWarning] = useState(false);

  const performLogout = useCallback(() => {
    clearAllTokens();
    clearUser();
    navigate('/login', {
      state: { message: 'Your session has expired. Please log in again.' },
    });
  }, [clearUser, navigate]);

  const extendSession = useCallback(async () => {
    try {
      // The refresh token travels via the httpOnly cookie; a 401 here means
      // the session is truly gone.
      const tokens = await AuthApiService.refreshToken();
      storeTokens(tokens.accessToken, tokens.expiresIn, tokens.refreshExpiresIn);
      setShowingExpiryWarning(false);
    } catch {
      performLogout();
    }
  }, [performLogout]);

  useEffect(() => {
    const hasToken = localStorage.getItem('welynk_access_token');
    if (!hasToken) return;

    if (isRefreshTokenExpired()) {
      performLogout();
      return;
    }

    intervalRef.current = setInterval(() => {
      if (isRefreshTokenExpired()) {
        performLogout();
        return;
      }

      const remainingMs = getRefreshTokenRemainingMs();
      if (remainingMs > 0 && remainingMs <= WARN_BEFORE_MS) {
        setShowingExpiryWarning(true);
      } else {
        setShowingExpiryWarning(false);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [performLogout]);

  return { performLogout, showingExpiryWarning, extendSession };
};
